import React, { FC, useEffect, useState } from 'react';
import { IoChevronDown } from 'react-icons/io5';
import { handleRipple } from '../../../helpers/ripple';
import {
  IconButton,
  NavbarSection,
  NavbarList,
  NavbarLink,
  NavbarDropdownTrigger,
  NavbarChildList,
  NavbarChildLink,
  LoginButton,
  BitcoinLogoWrapper,
} from './styled';
import ImageWithFallback from '../../ImageWithFallback';

interface NavbarItem {
  key: string;
  text: string;
  link: string | null;
  openNewTab: boolean;
  children: readonly (NavbarItem | null)[] | null;
}

interface ButtonData {
  buttonText: string | null | undefined;
  buttonLink: string | null | undefined;
}

interface Props {
  isOpen: boolean;
  onClick: () => void;
  navbarList: NavbarItem[];
  navbarMobileList: NavbarItem[];
  buttonData: ButtonData;
}

const Menu: FC<Props> = ({
  isOpen,
  onClick,
  navbarList,
  navbarMobileList,
  buttonData,
}) => {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  const handleButtonClick = () => {
    if (typeof onClick === 'function') {
      onClick();
    }
  };

  useEffect(() => {
    const overflowY = isOpen ? 'hidden' : '';
    document.documentElement.style.overflowY = overflowY;
    document.body.style.overflowY = overflowY;
    return () => {
      document.documentElement.style.overflowY = '';
      document.body.style.overflowY = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setExpandedKey(null);
    }
  }, [isOpen]);

  const toggleExpanded = (key: string) => {
    setExpandedKey(prev => (prev === key ? null : key));
  };

  const renderItem = (item: NavbarItem) => {
    const validChildren = (item.children ?? []).filter(
      (c): c is NavbarItem => c !== null,
    );
    const hasChildren = validChildren.length > 0;

    if (hasChildren) {
      const isExpanded = expandedKey === item.key;
      return (
        <React.Fragment key={item.key}>
          <NavbarDropdownTrigger
            type="button"
            onClick={() => toggleExpanded(item.key)}
            aria-expanded={isExpanded}
          >
            {item.text}
            <IoChevronDown
              size={20}
              style={{
                marginLeft: '8px',
                transition: 'transform 0.2s ease',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </NavbarDropdownTrigger>
          {isExpanded && (
            <NavbarChildList>
              {validChildren.map(child => (
                <NavbarChildLink
                  key={child.key}
                  href={child.link ?? undefined}
                  target={child.openNewTab ? '_blank' : undefined}
                  rel={child.openNewTab ? 'noopener noreferrer' : undefined}
                  onClick={handleButtonClick}
                >
                  {child.text}
                </NavbarChildLink>
              ))}
            </NavbarChildList>
          )}
        </React.Fragment>
      );
    }

    return (
      <NavbarLink
        key={item.key}
        href={item.link ?? undefined}
        target={item.openNewTab ? '_blank' : undefined}
        rel={item.openNewTab ? 'noopener noreferrer' : undefined}
        onClick={handleButtonClick}
      >
        {item.text}
      </NavbarLink>
    );
  };

  return (
    <>
      <IconButton onClick={handleButtonClick} aria-label="Toggle menu">
        {isOpen ? (
          <img src="/images/uploads/close.svg" alt="close" width={40} height={40} />
        ) : (
          <img src="/images/uploads/hamburger.svg" alt="hamburger" width={40} height={40} />
        )}
      </IconButton>
      <NavbarSection open={isOpen}>
        <NavbarList>
          {navbarList.map(item => renderItem(item))}
          {navbarMobileList.map(item => renderItem(item))}
        </NavbarList>
        <LoginButton
          href={buttonData.buttonLink ?? undefined}
          target="_blank"
          onMouseEnter={handleRipple}
        >
          <BitcoinLogoWrapper>
            <ImageWithFallback
              src="/images/uploads/floating-button-icon.png"
              alt="bitcoin logo"
            />
          </BitcoinLogoWrapper>
          {buttonData.buttonText}
        </LoginButton>
      </NavbarSection>
    </>
  );
};

export default Menu;
